<template>
	<div :class="containerClass">
		<div class="mx-auto max-w-4xl text-center">
			<!-- Urgency/Scarcity Indicator -->
			<div
				v-if="urgencyText"
				class="inline-flex items-center px-4 py-2 mb-6 text-sm font-medium text-red-800 bg-red-100 rounded-full"
			>
				<svg class="mr-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
					/>
				</svg>
				{{ urgencyText }}
			</div>

			<!-- Headline -->
			<h2 v-if="headline" :class="headlineClass">
				{{ headline }}
			</h2>

			<!-- Description -->
			<p v-if="description" :class="descriptionClass">
				{{ description }}
			</p>

			<div
				v-if="valueProps && valueProps.length > 0"
				class="grid grid-cols-1 gap-6 mb-8 md:grid-cols-3"
			>
				<div
					v-for="(prop, index) in valueProps"
					:key="index"
					class="p-4 rounded-lg bg-white/10 transition-all duration-200"
					:class="{ 'cursor-pointer hover:bg-white/20 hover:scale-[1.02] active:scale-[0.98]': makeValuePropsClickable }"
					@click="handleValuePropClick(prop, index)"
				>
					<div class="flex justify-center mb-2 text-3xl">
						<AppIcon
							v-if="prop.icon && prop.icon.startsWith('lucide:')"
							:name="prop.icon"
							size="2xl"
						/>
						<span v-else>{{ prop.icon }}</span>
					</div>
					<h3 class="mb-2 font-semibold">
						{{ prop.title }}
					</h3>
					<p class="text-sm opacity-80">
						{{ prop.description }}
					</p>
				</div>
			</div>

			<!-- Price Display -->
			<div v-if="price" class="p-6 mx-auto mb-8 max-w-md rounded-lg bg-white/10">
				<div class="flex justify-between items-center">
					<span class="font-medium">{{ priceLabel || 'Preis' }}</span>
					<div class="text-right">
						<span v-if="originalPrice" class="block text-sm line-through opacity-60">{{
							originalPrice
						}}</span>
						<span class="text-2xl font-bold">{{ price }}</span>
					</div>
				</div>
				<p v-if="priceNote" class="mt-2 text-sm opacity-80">
					{{ priceNote }}
				</p>
			</div>

			<!-- CTA Buttons -->
			<div class="flex flex-col gap-4 justify-center mb-8 sm:flex-row">
				<!-- Primary CTA -->
				<TrackClick
					v-if="primaryCTA"
					event="cta_clicked"
					category="cta"
					cta-type="primary"
					:cta-location="ctaLocation"
					:page-section="pageSection"
					:event-data="{
						cta_text: primaryCTA.text,
						variant: variant,
						urgency: urgencyText,
						price: price,
					}"
					tag="button"
					:class="primaryCtaClass"
					@click="handlePrimaryCtaClick"
				>
					{{ primaryCTA.text }}
				</TrackClick>

				<!-- Secondary CTA -->
				<TrackClick
					v-if="secondaryCTA"
					event="cta_clicked"
					category="cta"
					cta-type="secondary"
					:cta-location="ctaLocation"
					:page-section="pageSection"
					:event-data="{
						cta_text: secondaryCTA.text,
						variant: variant,
					}"
					tag="button"
					:class="secondaryCtaClass"
					@click="handleSecondaryCtaClick"
				>
					{{ secondaryCTA.text }}
				</TrackClick>
			</div>

			<!-- Payment Methods -->
			<div v-if="showPaymentMethods" class="mb-6">
				<PaymentMethodBadges
					:methods="paymentMethods"
					:show-label="showPaymentMethodsLabel"
					:label="paymentMethodsLabel"
					:show-secure="showSecurePayment"
				/>
			</div>

			<!-- Trust Indicators -->
			<div v-if="trustIndicators && trustIndicators.length > 0" class="text-sm opacity-80">
				<div class="flex flex-wrap gap-4 justify-center items-center">
					<div v-for="(indicator, index) in trustIndicators" :key="index" class="flex items-center">
						<AppIcon name="lucide:check-circle" size="sm" class="mr-1 text-green-300" />
						<span>{{ indicator }}</span>
					</div>
				</div>
			</div>

			<!-- Countdown Timer -->
			<div v-if="countdown" class="mt-8">
				<MarketingCountdown
					:end-date="countdown.endDate"
					:title="countdown.title"
					:variant="countdown.variant"
				/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue';
import TrackClick from '@/components/tracking/TrackClick.vue';
import MarketingCountdown from './MarketingCountdown.vue';
import PaymentMethodBadges from './PaymentMethodBadges.vue';
import AppIcon from '@/components/icons/AppIcon.vue';
import { useComponentTracking } from '@/composables/useComponentTracking';
import type { ValueProp, CTAButton } from './types';

export interface Countdown {
	endDate: Date;
	title?: string;
	variant?: 'default' | 'urgent';
}

interface Props {
	// Content
	headline?: string;
	description?: string;
	valueProps?: ValueProp[];
	urgencyText?: string;

	// Pricing
	price?: string;
	originalPrice?: string;
	priceLabel?: string;
	priceNote?: string;

	// CTAs
	primaryCTA?: CTAButton;
	secondaryCTA?: CTAButton;
	primaryButtonVariant?: 'default' | 'green';

	// Trust & Social Proof
	trustIndicators?: string[];
	countdown?: Countdown;

	// Payment Methods
	showPaymentMethods?: boolean;
	paymentMethods?: ('visa' | 'mastercard' | 'paypal' | 'card')[];
	showPaymentMethodsLabel?: boolean;
	paymentMethodsLabel?: string;
	showSecurePayment?: boolean;

	// Styling
	variant?: 'default' | 'urgent' | 'celebration' | 'minimal';
	size?: 'sm' | 'md' | 'lg';

	// Tracking
	ctaLocation?: string;
	pageSection?: string;
	// Behavior
	makeValuePropsClickable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	variant: 'default',
	size: 'md',
	ctaLocation: 'cta-section',
	pageSection: 'conversion',
	showPaymentMethods: false,
	paymentMethods: () => ['visa', 'mastercard', 'paypal'],
	showPaymentMethodsLabel: false,
	paymentMethodsLabel: 'Wir akzeptieren:',
	showSecurePayment: true,
	primaryButtonVariant: 'default',
	makeValuePropsClickable: false,
});

const emit = defineEmits<{
	'primary-cta-click': [cta: CTAButton];
	'secondary-cta-click': [cta: CTAButton];
	'value-prop-click': [prop: ValueProp, index: number];
}>();

function handleValuePropClick(prop: ValueProp, index: number) {
	if (props.makeValuePropsClickable) {
		// Track value prop click
		trackCtaClick(`Value Prop: ${prop.title}`, {
			variant: props.variant,
			cta_type: 'value_prop',
			section: 'value_props',
		});

		// Emit specific event
		emit('value-prop-click', prop, index);

		// If explicitly requested to act like CTA, also trigger primary default behavior
		// Note: The parent component should usually handle this via the event,
		// but we can also trigger the primary CTA click handler if it exists
		if (props.primaryCTA) {
			handlePrimaryCtaClick();
		}
	}
}

/**
 * Component Tracking
 * Follows Dependency Injection pattern - tracking is injected as a composable
 * Auto-includes marketing context (landing page, personalization) in all events
 * Follows SOLID principles:
 * - Single Responsibility: Component renders, tracking delegated to composable
 * - Open/Closed: Easy to extend with new tracking metrics
 * - Dependency Injection: Composable injected as dependency
 */
const { trackComponentView, trackCtaClick } = useComponentTracking({
	componentName: 'MarketingCTA',
	sectionId: props.pageSection || 'conversion',
	sectionType: 'cta',
});

// Track component view on mount
onMounted(() => {
	trackComponentView();
});

// Computed classes
const containerClass = computed(() => {
	const variants = {
		default: 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white',
		urgent: 'bg-gradient-to-r from-red-500 to-red-600 text-white',
		celebration: 'bg-gradient-to-r from-green-500 to-green-600 text-white',
		minimal: 'bg-gray-50 text-gray-900',
	};

	const sizes = {
		sm: 'py-8',
		md: 'py-12',
		lg: 'py-16',
	};

	return `${variants[props.variant]} ${sizes[props.size]} px-4`;
});

const headlineClass = computed(() => {
	const sizes = {
		sm: 'text-2xl md:text-3xl',
		md: 'text-3xl md:text-4xl',
		lg: 'text-4xl md:text-5xl',
	};

	return `${sizes[props.size]} font-bold mb-4`;
});

const descriptionClass = computed(() => {
	const color = props.variant === 'minimal' ? 'text-gray-600' : 'opacity-90';
	return `text-lg ${color} mb-8 max-w-2xl mx-auto`;
});

const primaryCtaClass = computed(() => {
	const base =
		'px-8 py-4 font-semibold text-lg rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300';

	if (props.variant === 'minimal') {
		return `${base} bg-blue-600 hover:bg-blue-700 text-white`;
	}

	if (props.primaryButtonVariant === 'green') {
		return `${base} bg-gradient-to-r from-green-400 to-green-500 text-white hover:text-blue-900 hover:from-green-500 hover:to-green-600`;
	}

	return `${base} bg-white text-blue-600 hover:bg-gray-50`;
});

const secondaryCtaClass = computed(() => {
	const base = 'px-8 py-4 font-medium text-lg rounded-xl border-2 transition-all duration-300';

	if (props.variant === 'minimal') {
		return `${base} border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50`;
	}

	return `${base} border-white/30 text-white hover:bg-white/10`;
});

/**
 * Handle primary CTA click
 * Follows Single Responsibility Principle:
 * - Tracking is delegated to composables
 * - Event emission is handled by Vue
 */
function handlePrimaryCtaClick(): void {
	// Track CTA click with component context
	const trackingData: Record<string, string | number | boolean> = {
		variant: props.variant,
		cta_type: 'primary',
	};

	// Only add optional fields if they have values
	if (props.urgencyText) {
		trackingData.urgency = props.urgencyText;
	}
	if (props.price) {
		trackingData.price = props.price;
	}

	trackCtaClick(props.primaryCTA?.text || 'Primary CTA', trackingData);

	// Emit event for parent component
	if (props.primaryCTA) {
		emit('primary-cta-click', props.primaryCTA);
	}
}

/**
 * Handle secondary CTA click
 * Follows Single Responsibility Principle:
 * - Tracking is delegated to composables
 * - Event emission is handled by Vue
 */
function handleSecondaryCtaClick(): void {
	// Track CTA click with component context
	trackCtaClick(props.secondaryCTA?.text || 'Secondary CTA', {
		variant: props.variant,
		cta_type: 'secondary',
	});

	// Emit event for parent component
	if (props.secondaryCTA) {
		emit('secondary-cta-click', props.secondaryCTA);
	}
}
</script>
